// projectile-interp.ts — PURELY COSMETIC render-time sub-tick interpolation for
// projectiles, so they glide between the simulation's 12.5 updates/sec instead of
// skipping (the screen paints up to ~125/sec, so today a fast shot is redrawn ~10× in
// the same spot then jumps). ZERO simulation impact: this only READS each projectile's
// authoritative m_wx/m_wy and returns a DRAWN position for paint time — it never writes
// gob/sim state, so positions, hashes, savegames, lockstep and replays are untouched.
// (The presentation-only pattern mirrors the smooth map-scroll in inputui.ts.)
//
// Smear-free without bespoke dirty-rect surgery: the existing gobInvalidate "redraw the
// old location and the new location" machinery (simuiform.ts) computes a gob's damage
// rect from shotClippingBounds() and tracks it in m_trcBoundingLast. Because BOTH
// shotClippingBounds (invalidation) AND drawShotGob (draw) read the SAME cached interp
// position via projectileInterpPos(), the damaged region always matches where the sprite
// is actually blitted. beginProjectileInterp() forces kfGobRedraw whenever a projectile's
// interp position advances (≈ SUB_STEPS times per sim update), so the invalidate/redraw
// pass animates it between sim steps.
//
// Pixel-oracle safe: a projectile is drawn at its true sim position on first sight
// (no history yet) and the static-frame goldens load PAUSED (no sim step) — so the
// interp is identity there and the TS frame still matches the WASM client. Only live
// motion diverges from WASM (which can't interpolate), exactly like the smooth-scroll
// precedent.

import {
  kgtTankShot,
  kgtRocket,
  kgtBullet,
  kgtArtilleryShot,
  kgtAndyShot,
} from '../../../sim/src/gob/gobtypes.ts';

const kfGobRedraw = 0x2; // must match simdraw.ts / simuiform.ts
// Sub-positions shown per sim update. 0 = CONTINUOUS (max): glide at the display/paint
// rate (the drawn position advances every paint). A positive N quantizes to N steps per
// update (e.g. 4 → 50 positions/sec). NOTE: smoothness is only realized if paints actually
// fire between sim updates — see the kfRedrawMax driver in gamesession.advanceTicks.
const SUB_STEPS = 0;

// Adaptive glide-cadence governor (issue #11: combat FPS drop while projectiles are airborne).
// PR #166's interpolation makes gamesession.advanceTicks force a repaint at DISPLAY rate while
// any projectile is airborne (kfRedrawMax) — ~100 paints/sec vs the ~12.5 on-demand paints/sec
// pre-#166. That ~8x multiplier on the per-paint cost blows the frame budget whenever a paint is
// expensive, which shows up as choppy combat in BOTH reported ways:
//   • weak phone GPU (Infinix Hot 40i / Mali-G57) — each paint is fill-rate-expensive
//   • fast Mac, massive army vs a base            — each paint marks/draws many gobs
// So we back off ONLY when the device is demonstrably behind — measured by the DISPLAYED-FRAME
// INTERVAL the embedder reports via noteFrameIntervalMs (wall time between rendered frames).
//
// Why frame interval, not pump cost (PR #191's signal): a fill-rate-bound GPU can't rasterize the
// forced repaints fast enough, so the browser DELAYS requestAnimationFrame while our CPU-side pump
// time stays LOW — pump cost never crossed the threshold and the throttle never engaged (why #11
// stayed open on the phone). The frame interval is the ground truth for "is the device keeping up":
// a GPU- OR CPU-bound choke both stretch it, a fast device holds ~display rate.
//
// What we back off: the PAINT PACE (glidePaintIntervalMs → event.ts gcmsDisplayUpdate), not just a
// per-frame decision. advanceTicks drains many ticks per rAF and the pump paints once per allowed
// interval, so capping the pace to ~30 FPS drops the wasted sub-frame repaints (only the last is
// ever presented) — that, not merely painting on fewer rAFs, is what recovers a heavy scene. We
// also drop the readsDst after-image echo, and LATCH the throttle for the burst — throttling makes
// the frames cheap again, so relaxing on the recovered interval would drop us straight back into the
// choke (oscillation). The latch clears after a sustained combat lull (see beginProjectileInterp),
// so a recovered device re-probes at full rate on the next burst. NO device classification.
// Presentation-only — drawn positions still use the deterministic host-tick clock, so
// sim/hashes/replays/pixel-oracle are untouched. A node/oracle harness never calls
// noteFrameIntervalMs → interval 0 → never throttled → behavior identical to pre-#11.
const kSlowFrameMs = 22; // EMA displayed-frame interval above this (~<45 FPS) → device is behind
const kFrameEmaWeight = 0.1; // new-sample weight of the interval EMA
const kClampFrameMs = 50; // clamp each sample here: a real combat frame can blow past 100ms
// (a DevTools trace of the massive battle showed 370–590ms rAF
// callbacks) — IGNORING those hid the very slowness the governor
// must catch, so clamp instead: any frame ≥50ms reads as "behind"
// (~<20 FPS) without one monster frame dominating the EMA.
export const kDisplayUpdateDefault = 8; // event.ts gcmsDisplayUpdate default — paints paced to ~125 FPS
const kGlideFloorMs = 33; // throttled paint pacing ≈ 30 FPS
const kLullFramesToRelax = 60; // sustained no-projectile frames before the latch re-probes
let frameIntervalMs = 0; // EMA of the real inter-frame interval (ms)
let glideThrottled = false;
let lullFrames = 0;

/** The embedder's rAF loop reports the wall-clock interval (ms) since the previous rendered
 *  frame, once per frame (main.ts / the editor playhost). This is the displayed cadence — a GPU-
 *  or CPU-bound choke stretches it — and drives the adaptive throttle above. Each sample is
 *  clamped to kClampFrameMs so a single monster frame (or a tab-stall) can't dominate the EMA,
 *  while a genuinely slow run still reads as "behind"; the EMA's own smoothing means one clamped
 *  spike can't latch a capable device. Render-only. */
export function noteFrameIntervalMs(ms: number): void {
  if (ms <= 0) return;
  const sample = ms < kClampFrameMs ? ms : kClampFrameMs;
  frameIntervalMs =
    frameIntervalMs === 0
      ? sample
      : frameIntervalMs * (1 - kFrameEmaWeight) + sample * kFrameEmaWeight;
}

/** The minimum ms between paints the in-game pump should enforce (event.ts gcmsDisplayUpdate)
 *  WHILE combat is forcing repaints. Unthrottled this is the default (~125 FPS cap — the glide
 *  shows at display rate); once the governor latches (slow displayed cadence under combat load —
 *  see beginProjectileInterp) it rises to ~30 FPS. Capping the PACE (not just the per-frame
 *  decision) is what actually cuts the cost: advanceTicks drains many ticks per rAF and the
 *  pump paints once per allowed interval, so a coarser pace removes the wasted sub-frame repaints
 *  (only the last is ever presented). Render-only. */
export function glidePaintIntervalMs(): number {
  return glideThrottled ? kGlideFloorMs : kDisplayUpdateDefault;
}

/** True while the governor is throttling — the embedder restores the default paint pace when
 *  combat ends. Render-only. */
export function isGlideThrottled(): boolean {
  return glideThrottled;
}

interface InterpGob {
  GetId(): number;
  GetType(): number;
  GetFlags(): number;
  SetFlags(f: number): void;
  m_wx: number;
  m_wy: number;
}

// After-image (option A): also draw the projectile sprite at its last echoCount() distinct
// interp positions (semitransparent copies), so a fast shot reads as a short streak instead
// of a hopping dot. 0 = off (lead only); 1 = lead + one trailing copy; 2 = a 3-sprite trail.
// Driven by the same adaptive signal as the cadence cap: when the device is keeping up = 1;
// when throttled (slow GPU) = 0, which drops the per-paint readsDst blend (the most expensive
// thing the echo adds). The histX cap shrinks/grows safely as this flips (see the while-loop).
function echoCount(): number {
  return glideThrottled ? 0 : 1;
}

interface Entry {
  wxPrev: number;
  wyPrev: number; // position at the PREVIOUS observed sim step
  wxCur: number;
  wyCur: number; // position at the LATEST observed sim step
  tPrev: number;
  tStep: number; // HOST-TICK counts of those two steps (not wall time)
  drawnWx: number;
  drawnWy: number; // the lead (current) interp position drawn this frame
  histX: number[];
  histY: number[]; // recent distinct lead positions, oldest..newest (lead last);
  // length <= echoCount()+1 — the after-image trail to draw + damage
  seen: number; // last frame this gob was processed (prune key)
}

/** The gob types this module interpolates — the single source of truth for "is a projectile"
 *  (shared with the #11 diagnostic tools so they don't re-list the raw type numbers). */
export const PROJECTILE_TYPES: ReadonlySet<number> = new Set([
  kgtTankShot,
  kgtRocket,
  kgtBullet,
  kgtArtilleryShot,
  kgtAndyShot,
]);

function isProjectile(gt: number): boolean {
  return PROJECTILE_TYPES.has(gt);
}

const entries = new Map<number, Entry>();
let frameNo = 0;

/** Clear all interpolation state — call at game start so projectile ids never carry
 *  across games. */
export function resetProjectileInterp(): void {
  entries.clear();
  frameNo = 0;
  frameIntervalMs = 0;
  glideThrottled = false;
  lullFrames = 0; // fresh adaptive measurement per game
}

/** True while any projectile is being interpolated (i.e. on screen, under the cap). The
 *  pump uses this to request max-rate repaints (kfRedrawMax) so the glide actually shows —
 *  the in-game paint is otherwise on-demand (≈ sim rate) and the in-between positions would
 *  never be painted. Returns false over the cap (entries cleared) → no forced repaint. */
export function hasActiveProjectileInterp(): boolean {
  return entries.size > 0;
}

/**
 * Per-paint pass: update each visible projectile's interpolated draw position and force
 * kfGobRedraw on the ones that advanced, so the existing gobInvalidate/redraw loop
 * animates them. MUST run BEFORE that loop (it primes the cache projectileInterpPos reads
 * and sets the redraw flags it keys off).
 *
 * `nowTicks` is the HOST tick counter (1/100s ticks, HostGetTickCount) — NOT wall time.
 * Using the deterministic sim/host clock (not performance.now()) makes the draw a pure
 * function of sim-tick state: a PAUSED sim stops stepping, so the fraction below saturates
 * to 1 and projectiles freeze at their true position — which keeps repeated paints of a
 * fixed state byte-identical (the wide-window force-redraw invariant) and keeps the
 * pixel-oracle static frames identity.
 *
 * `interpolate` is false when the visible-gob set is SATURATED (at/over the FindVisibleGobs
 * cap). Interpolation moves a projectile between sim steps, which damages extra cells; the
 * marking pass only re-marks gobs IN the visible set, so over the cap a past-cap gob in a
 * damaged cell would be erased without being redrawn. So over the cap (big battles — also
 * the perf-sensitive case) we draw projectiles at their true sim position (no interp); the
 * gating clears state so projectileInterpPos falls back to m_wx/m_wy.
 */
export function beginProjectileInterp(
  visible: Iterable<InterpGob>,
  nowTicks: number,
  interpolate = true,
): void {
  if (!interpolate) {
    entries.clear();
    return;
  } // over the visible cap → no interp (see above)
  frameNo++;
  for (const g of visible) {
    if (!isProjectile(g.GetType())) continue;
    const id = g.GetId();
    const wx = g.m_wx,
      wy = g.m_wy;
    const e = entries.get(id);
    if (e === undefined) {
      // First sight: no history → draw at the true sim position (identity). This is what
      // keeps the static-frame pixel-oracle byte-identical to the WASM client.
      entries.set(id, {
        wxPrev: wx,
        wyPrev: wy,
        wxCur: wx,
        wyCur: wy,
        tPrev: nowTicks,
        tStep: nowTicks,
        drawnWx: wx,
        drawnWy: wy,
        histX: [wx],
        histY: [wy],
        seen: frameNo,
      });
      continue;
    }
    if (wx !== e.wxCur || wy !== e.wyCur) {
      // the sim stepped this projectile since we last looked → shift the 2-state history.
      e.wxPrev = e.wxCur;
      e.wyPrev = e.wyCur;
      e.wxCur = wx;
      e.wyCur = wy;
      e.tPrev = e.tStep;
      e.tStep = nowTicks;
    }
    e.seen = frameNo;
    // Self-calibrating sub-tick fraction: host ticks since the last step over the MEASURED
    // tick duration of the previous inter-step interval — so it adapts to any game speed
    // (incl. the F11 SP slider) without knowing it. Clamped to [0,1] (interpolate, never
    // extrapolate — no overshoot; a PAUSED sim saturates it to 1 → freeze at cur).
    // Quantized to SUB_STEPS positions per update.
    const dur = e.tStep - e.tPrev;
    const f = dur > 0 ? Math.min(Math.max((nowTicks - e.tStep) / dur, 0), 1) : 0;
    const fq = SUB_STEPS > 0 ? Math.floor(f * SUB_STEPS) / SUB_STEPS : f; // 0 → continuous (max)
    const dwx = Math.round(e.wxPrev + fq * (e.wxCur - e.wxPrev));
    const dwy = Math.round(e.wyPrev + fq * (e.wyCur - e.wyPrev));
    if (dwx !== e.drawnWx || dwy !== e.drawnWy) {
      e.drawnWx = dwx;
      e.drawnWy = dwy;
      // push the new lead onto the after-image trail (keep the last echoCount()+1 distinct
      // positions). echoCount() can drop 1→0 when the governor begins throttling mid-combat,
      // shrinking the cap under a just-pushed trail — so loop (not a single if) to collapse it.
      const cap = echoCount() + 1;
      e.histX.push(dwx);
      e.histY.push(dwy);
      while (e.histX.length > cap) {
        e.histX.shift();
        e.histY.shift();
      }
      g.SetFlags((g.GetFlags() | kfGobRedraw) >>> 0); // advanced → repaint trail at the new spot
    }
  }
  // Drop projectiles no longer visible (one frame of grace) so the map can't leak.
  for (const [id, e] of entries) if (e.seen < frameNo - 1) entries.delete(id);

  // Governor latch (see kSlowFrameMs). Only engage while combat is actually forcing repaints
  // (projectiles airborne) — that's when the displayed cadence reflects the forced-repaint load.
  // Latch on the first slow reading and hold it through the burst so throttling (which makes the
  // frames cheap again) can't relax us straight back into the choke. A sustained lull re-probes.
  if (entries.size === 0) {
    if (glideThrottled && ++lullFrames >= kLullFramesToRelax) {
      glideThrottled = false;
      frameIntervalMs = 0;
      lullFrames = 0;
    }
  } else {
    lullFrames = 0;
    if (!glideThrottled && frameIntervalMs > kSlowFrameMs) glideThrottled = true;
  }
}

/**
 * The cached interp DRAW position for a projectile this frame, or null (not a tracked
 * projectile → caller uses m_wx/m_wy). Read by BOTH shotClippingBounds (invalidation)
 * and drawShotGob (draw) so the damaged region and the blit always agree → no smear.
 */
export function projectileInterpPos(g: { GetId(): number }): { wx: number; wy: number } | null {
  const e = entries.get(g.GetId());
  return e === undefined ? null : { wx: e.drawnWx, wy: e.drawnWy };
}

/**
 * The full set of positions to DRAW the projectile sprite at this frame, oldest→lead
 * (back-to-front): the after-image echoes followed by the current lead. null = not a
 * tracked projectile (caller draws at m_wx/m_wy). drawShotGob blits at each; shotClipping-
 * Bounds unions the sprite bbox over all of them so the damage rect covers the whole trail
 * (→ no smear, same gobInvalidate path). With ECHOES=0 this is just [lead].
 */
export function projectileDrawPositions(g: {
  GetId(): number;
}): { wx: number; wy: number }[] | null {
  const e = entries.get(g.GetId());
  if (e === undefined) return null;
  const out: { wx: number; wy: number }[] = [];
  for (let i = 0; i < e.histX.length; i++) out.push({ wx: e.histX[i], wy: e.histY[i] });
  return out;
}
